import React, { useState, useEffect } from 'react'
import { 
  ChevronLeftIcon, 
  ChevronRightIcon,
  CalendarIcon
} from '@heroicons/react/24/outline'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'

const TradingCalendar = () => {
  const { user } = useAuth()
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedSymbol, setSelectedSymbol] = useState('')
  const [selectedSide, setSelectedSide] = useState('Both')
  const [selectedView, setSelectedView] = useState('Overview')
  const [trades, setTrades] = useState([])
  const [loading, setLoading] = useState(true)
  const [calendarData, setCalendarData] = useState({})
  const [monthlyStats, setMonthlyStats] = useState({ pnl: 0, trades: 0 })

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]

  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

  // Fetch trades for the current month
  useEffect(() => {
    const fetchTrades = async () => {
      if (!user) return
      
      setLoading(true)
      try {
        const year = currentDate.getFullYear()
        const month = currentDate.getMonth()
        const startDate = new Date(year, month, 1)
        const endDate = new Date(year, month + 1, 0)
        
        let response = await api.trades.getAll({
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0],
          symbol: selectedSymbol || undefined,
          side: selectedSide === 'Both' ? undefined : selectedSide.toLowerCase()
        })
        
        console.log('API response:', response)
        console.log('API response keys:', Object.keys(response || {}))
        console.log('API response.data:', response.data)
        console.log('API response.data keys:', Object.keys(response.data || {}))
        
        // If no trades found, try without date filter and show them in the calendar anyway
        if ((!response?.data?.trades || response.data.trades.length === 0) && !selectedSymbol && selectedSide === 'Both') {
          console.log('No trades found with date filter, trying without date filter...')
          const allTradesResponse = await api.trades.getAll({})
          console.log('API response without date filter:', allTradesResponse)
          console.log('All trades response.data:', allTradesResponse.data)
          console.log('Total trades in database:', allTradesResponse?.data?.trades?.length || 0)
          
          // If we have trades but not in this month, use all trades for now
          if (allTradesResponse?.data?.trades && allTradesResponse.data.trades.length > 0) {
            console.log('Using all trades since none found in current month')
            response = allTradesResponse
          }
        }
        
        console.log('Query params:', {
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0],
          symbol: selectedSymbol || undefined,
          side: selectedSide === 'Both' ? undefined : selectedSide.toLowerCase()
        })
        
        // Handle the correct API response structure
        let tradesArray = []
        if (response && response.data && response.data.trades) {
          tradesArray = Array.isArray(response.data.trades) ? response.data.trades : []
        } else if (response && response.trades) {
          tradesArray = Array.isArray(response.trades) ? response.trades : []
        } else if (response && response.data) {
          tradesArray = Array.isArray(response.data) ? response.data : []
        } else if (Array.isArray(response)) {
          tradesArray = response
        }
        
        console.log('Processed trades array:', tradesArray)
        console.log('Trades array length:', tradesArray.length)
        
        setTrades(tradesArray)
        processCalendarData(tradesArray)
      } catch (error) {
        console.error('Error fetching trades:', error)
        console.error('Error details:', error.response?.data)
        toast.error('Failed to load calendar data')
        setTrades([])
        setCalendarData({})
        setMonthlyStats({ pnl: 0, trades: 0 })
      } finally {
        setLoading(false)
      }
    }

    fetchTrades()
  }, [user, currentDate, selectedSymbol, selectedSide])

  // Process trades into calendar format
  const processCalendarData = (tradesData) => {
    const calendar = {}
    let monthlyPnL = 0
    let monthlyTrades = 0

    // Ensure tradesData is an array
    if (!Array.isArray(tradesData)) {
      console.warn('Trades data is not an array:', tradesData)
      setCalendarData({})
      setMonthlyStats({ pnl: 0, trades: 0 })
      return
    }

    tradesData.forEach(trade => {
      if (!trade || !trade.entry_time) {
        console.warn('Invalid trade data:', trade)
        return
      }

      const date = new Date(trade.entry_time).toISOString().split('T')[0]
      const pnl = trade.pnl || 0
      
      if (!calendar[date]) {
        calendar[date] = { pnl: 0, trades: 0, tradesList: [] }
      }
      
      calendar[date].pnl += pnl
      calendar[date].trades += 1
      calendar[date].tradesList.push(trade)
      
      monthlyPnL += pnl
      monthlyTrades += 1
    })

    // Add color coding
    Object.keys(calendar).forEach(date => {
      const dayData = calendar[date]
      dayData.color = dayData.pnl > 0 ? 'text-green-500' : dayData.pnl < 0 ? 'text-red-500' : 'text-gray-500'
    })

    setCalendarData(calendar)
    setMonthlyStats({ pnl: monthlyPnL, trades: monthlyTrades })
  }

  const getDaysInMonth = (date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1 // Monday = 0

    const days = []
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null)
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day)
    }
    
    return days
  }

  const formatDate = (year, month, day) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
  }

  const getTradeData = (day) => {
    if (!day) return null
    const dateStr = formatDate(currentDate.getFullYear(), currentDate.getMonth(), day)
    return calendarData[dateStr] || { pnl: 0, trades: 0, color: 'text-gray-500' }
  }

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev)
      newDate.setMonth(prev.getMonth() + direction)
      return newDate
    })
  }

  const formatPnL = (pnl) => {
    if (pnl === 0) return '$0'
    const sign = pnl > 0 ? '+' : ''
    return `${sign}$${Math.abs(pnl).toFixed(2)}`
  }

  const getWeeklyTotals = () => {
    const days = getDaysInMonth(currentDate)
    const weeks = []
    let currentWeek = []
    
    days.forEach((day, index) => {
      currentWeek.push(day)
      if ((index + 1) % 7 === 0 || index === days.length - 1) {
        weeks.push([...currentWeek])
        currentWeek = []
      }
    })
    
    return weeks.map((week, weekIndex) => {
      let weekPnL = 0
      let weekTrades = 0
      
      week.forEach(day => {
        if (day) {
          const data = getTradeData(day)
          if (data) {
            weekPnL += data.pnl
            weekTrades += data.trades
          }
        }
      })
      
      return {
        weekNumber: weekIndex + 1,
        pnl: weekPnL,
        trades: weekTrades,
        color: weekPnL > 0 ? 'text-green-500' : weekPnL < 0 ? 'text-red-500' : 'text-gray-500'
      }
    })
  }

  // Calculate win/loss metrics
  const getWinLossMetrics = () => {
    const winningTrades = trades.filter(trade => (trade.pnl || 0) > 0)
    const losingTrades = trades.filter(trade => (trade.pnl || 0) < 0)
    const breakEvenTrades = trades.filter(trade => (trade.pnl || 0) === 0)
    
    const totalWinAmount = winningTrades.reduce((sum, trade) => sum + (trade.pnl || 0), 0)
    const totalLossAmount = losingTrades.reduce((sum, trade) => sum + (trade.pnl || 0), 0)
    
    return {
      winningTrades: winningTrades.length,
      losingTrades: losingTrades.length,
      breakEvenTrades: breakEvenTrades.length,
      winRate: trades.length > 0 ? (winningTrades.length / trades.length * 100) : 0,
      avgWin: winningTrades.length > 0 ? totalWinAmount / winningTrades.length : 0,
      avgLoss: losingTrades.length > 0 ? totalLossAmount / losingTrades.length : 0,
      totalWinAmount,
      totalLossAmount,
      profitFactor: Math.abs(totalLossAmount) > 0 ? totalWinAmount / Math.abs(totalLossAmount) : 0
    }
  }

  // Calculate drawdown
  const getDrawdownData = () => {
    let runningPnL = 0
    let peak = 0
    let maxDrawdown = 0
    let currentDrawdown = 0
    
    const sortedTrades = [...trades].sort((a, b) => new Date(a.entry_time) - new Date(b.entry_time))
    
    const drawdownData = sortedTrades.map(trade => {
      runningPnL += (trade.pnl || 0)
      
      if (runningPnL > peak) {
        peak = runningPnL
        currentDrawdown = 0
      } else {
        currentDrawdown = peak - runningPnL
        if (currentDrawdown > maxDrawdown) {
          maxDrawdown = currentDrawdown
        }
      }
      
      return {
        date: trade.entry_time,
        pnl: runningPnL,
        drawdown: currentDrawdown,
        drawdownPercent: peak > 0 ? (currentDrawdown / peak * 100) : 0
      }
    })
    
    return { drawdownData, maxDrawdown, maxDrawdownPercent: peak > 0 ? (maxDrawdown / peak * 100) : 0 }
  }

  // Get symbol breakdown
  const getSymbolBreakdown = () => {
    const symbolStats = {}
    
    trades.forEach(trade => {
      const symbol = trade.symbol
      if (!symbolStats[symbol]) {
        symbolStats[symbol] = {
          trades: 0,
          pnl: 0,
          wins: 0,
          losses: 0
        }
      }
      
      symbolStats[symbol].trades += 1
      symbolStats[symbol].pnl += (trade.pnl || 0)
      
      if ((trade.pnl || 0) > 0) {
        symbolStats[symbol].wins += 1
      } else if ((trade.pnl || 0) < 0) {
        symbolStats[symbol].losses += 1
      }
    })
    
    return Object.entries(symbolStats).map(([symbol, stats]) => ({
      symbol,
      ...stats,
      winRate: stats.trades > 0 ? (stats.wins / stats.trades * 100) : 0,
      avgPnL: stats.trades > 0 ? stats.pnl / stats.trades : 0
    })).sort((a, b) => b.pnl - a.pnl)
  }

  const days = getDaysInMonth(currentDate)
  const weeklyTotals = getWeeklyTotals()
  const winLossMetrics = getWinLossMetrics()
  const { drawdownData, maxDrawdown, maxDrawdownPercent } = getDrawdownData()
  const symbolBreakdown = getSymbolBreakdown()

  // Get unique symbols from trades for filter
  const uniqueSymbols = [...new Set(trades.map(trade => trade.symbol))].sort()

  // Render different views based on selected tab
  const renderCalendarView = () => {
    switch (selectedView) {
      case 'Overview':
        return renderOverviewCalendar()
      case 'Detailed':
        return renderDetailedView()
      case 'Win vs Loss':
        return renderWinLossView()
      case 'Drawdown':
        return renderDrawdownView()
      case 'Compare':
        return renderCompareView()
      case 'Tag Breakdown':
        return renderTagBreakdownView()
      case 'Advanced':
        return renderAdvancedView()
      default:
        return renderOverviewCalendar()
    }
  }

  const renderOverviewCalendar = () => (
    <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl">
      <div className="border border-white/20 rounded-lg overflow-hidden shadow-sm">
        {/* Header Row */}
        <div className="grid grid-cols-8 bg-black/20 border-b border-white/10">
          {dayNames.map((day) => (
            <div key={day} className="p-3 text-sm font-medium text-gray-300 text-center border-r border-white/10 last:border-r-0">
              {day}
            </div>
          ))}
          <div className="p-3 text-sm font-medium text-gray-300 text-center">
            Total
          </div>
        </div>

        {/* No trades message */}
        {trades.length === 0 && (
          <div className="p-8 text-center text-gray-400">
            <p className="text-lg mb-2">No trades found for {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</p>
            <p className="text-sm">Try selecting a different month or add some trades to see them here.</p>
          </div>
        )}

        {/* Calendar Days */}
        {trades.length > 0 && Array.from({ length: Math.ceil(days.length / 7) }, (_, weekIndex) => {
          const weekStart = weekIndex * 7
          const weekDays = days.slice(weekStart, weekStart + 7)
          const weekTotal = weeklyTotals[weekIndex]
          
          return (
            <div key={weekIndex} className="grid grid-cols-8 border-b border-white/10 last:border-b-0">
              {weekDays.map((day, dayIndex) => {
                const tradeData = day ? getTradeData(day) : null
                
                return (
                  <div key={dayIndex} className="p-3 border-r border-white/10 min-h-[80px] hover:bg-white/5 transition-colors cursor-pointer last:border-r-0">
                    {day && (
                      <>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-white">{String(day).padStart(2, '0')}</span>
                          {tradeData?.trades > 0 && (
                            <span className="text-xs text-gray-400">{tradeData.trades} trades</span>
                          )}
                        </div>
                        {tradeData && tradeData.pnl !== 0 && (
                          <div className={`text-sm font-medium ${tradeData.pnl > 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {formatPnL(tradeData.pnl)}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )
              })}
              
              {/* Week Total */}
              <div className="p-3 bg-black/20 min-h-[80px]">
                <div className="text-sm font-medium text-white mb-1">
                  Week {weekTotal.weekNumber}
                </div>
                <div className={`text-sm font-medium ${weekTotal.pnl > 0 ? 'text-green-400' : weekTotal.pnl < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                  {formatPnL(weekTotal.pnl)}
                </div>
                <div className="text-xs text-gray-400">
                  {weekTotal.trades} trades
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )

  const renderDetailedView = () => (
    <div className="space-y-4">
      <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl">
        <div className="mb-6">
          <h3 className="text-xl font-bold text-white">Detailed Trade List</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-black/20">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Date</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Symbol</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Side</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Quantity</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Entry Price</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Exit Price</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">P&L</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {trades.map((trade) => (
                <tr key={trade.id} className="hover:bg-white/5 transition-colors duration-200">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                    {new Date(trade.entry_time).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">{trade.symbol}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border ${
                      trade.side === 'buy' 
                        ? 'bg-green-500/20 text-green-400 border-green-500/30'
                        : 'bg-red-500/20 text-red-400 border-red-500/30'
                    }`}>
                      {trade.side?.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{trade.quantity}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">${trade.entry_price}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    {trade.exit_price ? `$${trade.exit_price}` : '-'}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-bold ${
                    (trade.pnl || 0) > 0 ? 'text-green-400' : (trade.pnl || 0) < 0 ? 'text-red-400' : 'text-gray-300'
                  }`}>
                    {formatPnL(trade.pnl || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border ${
                      trade.status === 'closed' 
                        ? 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                        : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                    }`}>
                      {trade.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  const renderWinLossView = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl">
        <h3 className="text-xl font-bold text-white mb-6">Win/Loss Statistics</h3>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-gray-300">Winning Trades</span>
            <span className="text-green-400 font-semibold">{winLossMetrics.winningTrades}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-300">Losing Trades</span>
            <span className="text-red-400 font-semibold">{winLossMetrics.losingTrades}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-300">Break-even Trades</span>
            <span className="text-gray-400 font-semibold">{winLossMetrics.breakEvenTrades}</span>
          </div>
          <div className="flex justify-between items-center border-t border-white/10 pt-4">
            <span className="text-gray-300">Win Rate</span>
            <span className="text-indigo-400 font-semibold">{winLossMetrics.winRate.toFixed(1)}%</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-300">Average Win</span>
            <span className="text-green-400 font-semibold">{formatPnL(winLossMetrics.avgWin)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-300">Average Loss</span>
            <span className="text-red-400 font-semibold">{formatPnL(winLossMetrics.avgLoss)}</span>
          </div>
          <div className="flex justify-between items-center border-t border-white/10 pt-4">
            <span className="text-gray-300">Profit Factor</span>
            <span className="text-white font-semibold">{winLossMetrics.profitFactor.toFixed(2)}</span>
          </div>
        </div>
      </div>
      
      <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl">
        <h3 className="text-xl font-bold text-white mb-6">P&L Distribution</h3>
        <div className="space-y-4">
          <div>
            <div className="flex justify-between mb-2">
              <span className="text-sm text-gray-300">Total Wins</span>
              <span className="text-sm font-medium text-green-400">{formatPnL(winLossMetrics.totalWinAmount)}</span>
            </div>
            <div className="w-full bg-white/10 rounded-full h-2">
              <div 
                className="bg-green-500 h-2 rounded-full" 
                style={{ width: `${Math.abs(winLossMetrics.totalWinAmount) / (Math.abs(winLossMetrics.totalWinAmount) + Math.abs(winLossMetrics.totalLossAmount)) * 100}%` }}
              ></div>
            </div>
          </div>
          <div>
            <div className="flex justify-between mb-2">
              <span className="text-sm text-gray-300">Total Losses</span>
              <span className="text-sm font-medium text-red-400">{formatPnL(winLossMetrics.totalLossAmount)}</span>
            </div>
            <div className="w-full bg-white/10 rounded-full h-2">
              <div 
                className="bg-red-500 h-2 rounded-full" 
                style={{ width: `${Math.abs(winLossMetrics.totalLossAmount) / (Math.abs(winLossMetrics.totalWinAmount) + Math.abs(winLossMetrics.totalLossAmount)) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  const renderDrawdownView = () => (
    <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl">
      <h3 className="text-xl font-bold text-white mb-6">Drawdown Analysis</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4">
          <h4 className="text-sm font-medium text-red-400 mb-2">Maximum Drawdown</h4>
          <p className="text-2xl font-bold text-red-400">{formatPnL(maxDrawdown)}</p>
          <p className="text-sm text-red-400">{maxDrawdownPercent.toFixed(2)}%</p>
        </div>
        <div className="bg-indigo-500/20 border border-indigo-500/30 rounded-lg p-4">
          <h4 className="text-sm font-medium text-indigo-400 mb-2">Current P&L</h4>
          <p className="text-2xl font-bold text-indigo-400">{formatPnL(monthlyStats.pnl)}</p>
          <p className="text-sm text-indigo-400">{monthlyStats.trades} trades</p>
        </div>
      </div>
      {drawdownData.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-black/20">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Date</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Cumulative P&L</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Drawdown</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Drawdown %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {drawdownData.slice(-10).map((item, index) => (
                <tr key={index} className="hover:bg-white/5 transition-colors duration-200">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                    {new Date(item.date).toLocaleDateString()}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-bold ${
                    item.pnl >= 0 ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {formatPnL(item.pnl)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-red-400">
                    {formatPnL(-item.drawdown)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-red-400">
                    {item.drawdownPercent.toFixed(2)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )

  const renderCompareView = () => (
    <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl">
      <h3 className="text-xl font-bold text-white mb-6">Symbol Comparison</h3>
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-black/20">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Symbol</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Trades</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Total P&L</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Avg P&L</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Win Rate</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Wins</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Losses</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {symbolBreakdown.map((item) => (
              <tr key={item.symbol} className="hover:bg-white/5 transition-colors duration-200">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">{item.symbol}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{item.trades}</td>
                <td className={`px-6 py-4 whitespace-nowrap text-sm font-bold ${
                  item.pnl >= 0 ? 'text-green-400' : 'text-red-400'
                }`}>
                  {formatPnL(item.pnl)}
                </td>
                <td className={`px-6 py-4 whitespace-nowrap text-sm ${
                  item.avgPnL >= 0 ? 'text-green-400' : 'text-red-400'
                }`}>
                  {formatPnL(item.avgPnL)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-indigo-400">
                  {item.winRate.toFixed(1)}%
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-green-400">{item.wins}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-red-400">{item.losses}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )

  const renderTagBreakdownView = () => (
    <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl">
      <h3 className="text-xl font-bold text-white mb-6">Tag Breakdown</h3>
      <div className="text-center py-8 text-gray-400">
        <p>Tag analysis will be available when trades include strategy tags.</p>
        <p className="text-sm mt-2">Add strategy or setup_type information to your trades to see tag breakdown.</p>
      </div>
    </div>
  )

  const renderAdvancedView = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-6 shadow-2xl">
          <h4 className="text-sm font-medium text-gray-300 mb-2">Risk Metrics</h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-xs text-gray-400">Sharpe Ratio:</span>
              <span className="text-xs font-medium text-white">N/A</span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-gray-400">Max Drawdown:</span>
              <span className="text-xs font-medium text-red-400">{formatPnL(maxDrawdown)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-gray-400">Profit Factor:</span>
              <span className="text-xs font-medium text-white">{winLossMetrics.profitFactor.toFixed(2)}</span>
            </div>
          </div>
        </div>
        
        <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-6 shadow-2xl">
          <h4 className="text-sm font-medium text-gray-300 mb-2">Performance</h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-xs text-gray-400">Total Return:</span>
              <span className={`text-xs font-medium ${monthlyStats.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {formatPnL(monthlyStats.pnl)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-gray-400">Win Rate:</span>
              <span className="text-xs font-medium text-indigo-400">{winLossMetrics.winRate.toFixed(1)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-gray-400">Avg Trade:</span>
              <span className="text-xs font-medium text-white">
                {monthlyStats.trades > 0 ? formatPnL(monthlyStats.pnl / monthlyStats.trades) : '$0'}
              </span>
            </div>
          </div>
        </div>
        
        <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-6 shadow-2xl">
          <h4 className="text-sm font-medium text-gray-300 mb-2">Activity</h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-xs text-gray-400">Total Trades:</span>
              <span className="text-xs font-medium text-white">{monthlyStats.trades}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-gray-400">Symbols Traded:</span>
              <span className="text-xs font-medium text-white">{uniqueSymbols.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-gray-400">Trading Days:</span>
              <span className="text-xs font-medium text-white">{Object.keys(calendarData).length}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="relative min-h-full flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="xl" />
          <p className="mt-4 text-gray-300">Loading calendar data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="relative min-h-full">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse"></div>
      </div>
      
      <div className="relative p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-white via-indigo-200 to-purple-200 bg-clip-text text-transparent">
                Trading Calendar 📅
              </h1>
              <p className="text-gray-300 mt-1">
                Comprehensive view of your trading performance over time
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 mb-8 shadow-2xl">
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Symbol</label>
                <select 
                  value={selectedSymbol}
                  onChange={(e) => setSelectedSymbol(e.target.value)}
                  className="border border-white/20 bg-white/10 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-white backdrop-blur-xl"
                >
                  <option value="">All Symbols</option>
                  {uniqueSymbols.map(symbol => (
                    <option key={symbol} value={symbol}>{symbol}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Side</label>
                <select 
                  value={selectedSide}
                  onChange={(e) => setSelectedSide(e.target.value)}
                  className="border border-white/20 bg-white/10 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-white backdrop-blur-xl"
                >
                  <option value="Both">Both</option>
                  <option value="Long">Long</option>
                  <option value="Short">Short</option>
                </select>
              </div>
            </div>

            {/* View tabs */}
            <div className="flex space-x-1 bg-black/20 rounded-lg p-1 w-fit">
              {['Overview', 'Detailed', 'Win vs Loss', 'Drawdown', 'Compare', 'Tag Breakdown', 'Advanced'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setSelectedView(tab)}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${
                    tab === selectedView ? 'bg-indigo-500 text-white shadow-sm' : 'text-gray-300 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          {/* Calendar Navigation */}
          <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 mb-8 shadow-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button 
                  onClick={() => navigateMonth(-1)}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors"
                >
                  <ChevronLeftIcon className="h-5 w-5 text-gray-300" />
                </button>
                <h2 className="text-xl font-semibold text-white min-w-[200px] text-center">
                  {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                </h2>
                <button 
                  onClick={() => navigateMonth(1)}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors"
                >
                  <ChevronRightIcon className="h-5 w-5 text-gray-300" />
                </button>
              </div>
              <div className={`text-sm font-medium ${monthlyStats.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                Monthly P&L: {formatPnL(monthlyStats.pnl)} ({monthlyStats.trades} trades)
                {trades.length > 0 && Object.keys(calendarData).length === 0 && (
                  <span className="text-xs text-gray-400 block">Showing all trades (none in current month)</span>
                )}
              </div>
            </div>
          </div>

          {/* Calendar/Analysis Views */}
          <div className="mb-8">
            {renderCalendarView()}
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-6 shadow-2xl">
              <h3 className="text-sm font-medium text-gray-300 mb-2">Total Trades</h3>
              <p className="text-2xl font-bold text-white">{monthlyStats.trades}</p>
            </div>
            <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-6 shadow-2xl">
              <h3 className="text-sm font-medium text-gray-300 mb-2">Monthly P&L</h3>
              <p className={`text-2xl font-bold ${monthlyStats.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {formatPnL(monthlyStats.pnl)}
              </p>
            </div>
            <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-6 shadow-2xl">
              <h3 className="text-sm font-medium text-gray-300 mb-2">Avg P&L per Trade</h3>
              <p className={`text-2xl font-bold ${monthlyStats.trades > 0 && monthlyStats.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {monthlyStats.trades > 0 ? formatPnL(monthlyStats.pnl / monthlyStats.trades) : '$0'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TradingCalendar